<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Table from '../../Table.svelte';
  import MultiSelect from '../../MultiSelect.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import DateRange from '../../DateRange.svelte';
  import Pagination from '../../Pagination.svelte';
  import Button from '../../Button.svelte';
  import AsyncSearchableDropdown from '../../AsyncSearchableDropdown.svelte';
  import { DonationService, type DonationRequest } from '../../../lib/api/admin/donation.service';
  import { getStageMeta, stageFromStatusName, STAGE_LABEL, type LifecycleStage } from '../../../lib/donationLifecycle';
  import employersSearchService from '../../../lib/api/admin/employers-search.service';
  import charitiesSearchService from '../../../lib/api/admin/charities-search.service';
  import employeesSearchService from '../../../lib/api/admin/employees-search.service';
  import { createPreferencesManager } from '../../../lib/utils/preferences';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import EmptyState from '../../EmptyState.svelte';
  import ErrorState from '../../ErrorState.svelte';

  // Preferences manager
  const preferences = createPreferencesManager('donation-list');
  
  // State
  let loading = false;
  let error: string | null = null;
  let tableData: DonationRequest[] = [];
  let totalPages = 1;

  // Metrics
  let totalDonations = 0;
  let pendingDonations = 0;
  let approvedDonations = 0;
  let totalDonationValue = 0;

  // Filter states - initialize from preferences
  const savedFilters = preferences.getFilters();
  let selectedStatuses: string[] = savedFilters.selectedStatuses || [];
  let selectedEmployer = savedFilters.selectedEmployer || '';
  let selectedCharity = savedFilters.selectedCharity || '';
  let selectedEmployee = savedFilters.selectedEmployee || '';
  let startDate = savedFilters.startDate || '';
  let endDate = savedFilters.endDate || '';
  let searchPhrase = savedFilters.searchPhrase || '';
  
  // Pagination - initialize from preferences
  let currentPage = preferences.getCurrentPage() || 1;
  let itemsPerPage = preferences.getPageSize() || 10;

  // Canonical lifecycle status options (single source: donationLifecycle.ts). The filter sends
  // lifecycle_stage (the option uuid IS the stage key), so labels/values can never drift.
  const statusOptionsList: Array<{uuid: string, name: string}> = (
    ['submitted', 'approved', 'employer_paid', 'paid_to_charity', 'received_by_charity', 'rejected', 'awaiting_employer_registration'] as const
  ).map((s) => ({ uuid: s, name: STAGE_LABEL[s] }));

  // Search functions for async dropdowns
  async function searchEmployers(query: string): Promise<Array<{uuid: string, name: string}>> {
    try {
      if (!query.trim()) return [];
      return await employersSearchService.searchEmployers(query, 10);
    } catch (err) {
      console.error('Error searching employers:', err);
      return [];
    }
  }

  async function getEmployerLabel(uuid: string): Promise<string | null> {
    try {
      return await employersSearchService.getEmployerName(uuid);
    } catch (err) {
      console.error('Error getting employer name:', err);
      return null;
    }
  }

  async function searchCharities(query: string): Promise<Array<{uuid: string, name: string}>> {
    try {
      if (!query.trim()) return [];
      return await charitiesSearchService.searchCharities(query, 10);
    } catch (err) {
      console.error('Error searching charities:', err);
      return [];
    }
  }

  async function getCharityLabel(uuid: string): Promise<string | null> {
    try {
      return await charitiesSearchService.getCharityName(uuid);
    } catch (err) {
      console.error('Error getting charity name:', err);
      return null;
    }
  }

  async function searchEmployees(query: string): Promise<Array<{uuid: string, name: string}>> {
    try {
      if (!query.trim()) return [];
      return await employeesSearchService.searchEmployees(query, 10);
    } catch (err) {
      console.error('Error searching employees:', err);
      return [];
    }
  }

  async function getEmployeeLabel(uuid: string): Promise<string | null> {
    try {
      // The service now caches names from search results
      // If not in cache, it will try to search (which might not work for UUID)
      // But in practice, the name should be cached when user selects from dropdown
      return await employeesSearchService.getEmployeeName(uuid);
    } catch (err) {
      console.error('Error getting employee name:', err);
      // Return null gracefully - the dropdown will show the UUID if name is not available
      return null;
    }
  }

  // Handle filter changes
  function handleStatusChange(event: CustomEvent<string[]>) {
    selectedStatuses = event.detail;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { selectedStatuses, selectedEmployer, selectedCharity, selectedEmployee, startDate, endDate, searchPhrase } });
    loadData();
  }

  function handleEmployerChange(value: string) {
    selectedEmployer = value;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { selectedStatuses, selectedEmployer, selectedCharity, selectedEmployee, startDate, endDate, searchPhrase } });
    loadData();
  }

  function handleCharityChange(value: string) {
    selectedCharity = value;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { selectedStatuses, selectedEmployer, selectedCharity, selectedEmployee, startDate, endDate, searchPhrase } });
    loadData();
  }

  function handleEmployeeChange(value: string) {
    selectedEmployee = value;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { selectedStatuses, selectedEmployer, selectedCharity, selectedEmployee, startDate, endDate, searchPhrase } });
    
    // Cache the employee name if we have it from search results
    // This helps when the component needs to display the selected label later
    if (value) {
      // Try to find the employee in recent search results and cache it
      // The search function already caches results, so this is mainly for edge cases
    }
    
    loadData();
  }

  function handleDateRangeChange(event: CustomEvent<{startDate: string, endDate: string}>) {
    startDate = event.detail.startDate;
    endDate = event.detail.endDate;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { selectedStatuses, selectedEmployer, selectedCharity, selectedEmployee, startDate, endDate, searchPhrase } });
    loadData();
  }

  function handleResetFilters() {
    selectedStatuses = [];
    selectedEmployer = '';
    selectedCharity = '';
    selectedEmployee = '';
    startDate = '';
    endDate = '';
    searchPhrase = '';
    currentPage = 1;
    preferences.reset();
    loadData();
  }
  
  // Watch for filter changes and save preferences
  $: if (selectedStatuses !== undefined || selectedEmployer !== undefined || selectedCharity !== undefined || 
         selectedEmployee !== undefined || startDate !== undefined || endDate !== undefined || searchPhrase !== undefined) {
    preferences.updateFilters({ 
      selectedStatuses, selectedEmployer, selectedCharity, selectedEmployee, 
      startDate, endDate, searchPhrase 
    });
  }
  
  // Watch for page size changes
  $: if (itemsPerPage !== undefined) {
    preferences.update({ pageSize: itemsPerPage });
  }
  
  // Watch for page changes
  $: if (currentPage !== undefined) {
    preferences.update({ currentPage });
  }

  function handleRowClick(row: DonationRequest) {
    push(`/view-donation/${row.uuid}`);
  }

  // Table columns
  const columns = [
    {
      key: 'user',
      label: 'Employee Name',
      width: '20%',
      render: (value: any, row: DonationRequest) => `
        <div class="employee-info">
          <span>${row.user?.full_name || '—'}</span>
        </div>
      `
    },
    {
      key: 'employer',
      label: 'Employer',
      width: '20%',
      render: (value: any, row: DonationRequest) => row.employer?.name || '—'
    },
    {
      key: 'leave_hours',
      label: 'Leave Hours',
      width: '15%',
      render: (value: number) => `${value}h`
    },
    {
      key: 'charity',
      label: 'Charity',
      width: '20%',
      render: (value: any, row: DonationRequest) => row.charity?.name || '—'
    },
    {
      key: 'donation_request_status',
      label: 'Status',
      width: '220px',
      align: 'center',
      render: (_value: any, row: DonationRequest) => renderStageBadge(row)
    }
  ];

  // Single canonical lifecycle status, replacing the old raw-status + separate charity-receipt
  // columns. Prefers the backend lifecycle_stage. Colour + label come from donationLifecycle.ts.
  function renderStageBadge(row: DonationRequest): string {
    const stage = (row.lifecycle_stage as LifecycleStage)
      || stageFromStatusName(row.donation_request_status?.name);
    const meta = getStageMeta(stage);
    return `<span class="lc-badge ${meta.badgeClass}">${stageBadgeIcon(meta.tone)}<span>${meta.label}</span></span>`;
  }

  function stageBadgeIcon(tone: string): string {
    if (tone === 'red') {
      return '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>';
    }
    if (tone === 'amber' || tone === 'grey') {
      return '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/></svg>';
    }
    return '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>';
  }

  async function loadData() {
    loading = true;
    error = null;
    try {
      const filters: any = {
        page: currentPage,
        per_page: itemsPerPage
      };

      if (searchPhrase) filters.phrase = searchPhrase;
      if (selectedEmployer) filters.employer_uuid = selectedEmployer;
      if (selectedCharity) filters.charity_uuid = selectedCharity;
      if (selectedEmployee) filters.user_uuid = selectedEmployee;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      
      // Filter by canonical lifecycle stage (comma-separated for multi-select).
      if (selectedStatuses.length > 0) {
        filters.lifecycle_stage = selectedStatuses.join(',');
      }

      const response = await DonationService.getDonations(filters);
      
      if (response.success) {
        const rawData = response.data;
        const isPaginated = rawData && typeof rawData === 'object' && 'items' in rawData;
        tableData = Array.isArray(rawData) ? rawData : (isPaginated && Array.isArray((rawData as { items: unknown[] }).items) ? (rawData as { items: typeof tableData }).items : []);
        const p = (response as { paging?: { last_page?: number; per_page?: number; total?: number } }).paging ?? (isPaginated && rawData && typeof rawData === 'object' && 'paging' in rawData ? (rawData as { paging?: { last_page?: number; per_page?: number; total?: number } }).paging : undefined);
        const summary = (response as { summary?: { total_requests: number; pending_requests: number; approved_requests: number; total_value: number } }).summary
          ?? (isPaginated && rawData && typeof rawData === 'object' && 'summary' in rawData
            ? (rawData as { summary?: { total_requests: number; pending_requests: number; approved_requests: number; total_value: number } }).summary
            : undefined);
        totalPages = (p && typeof p === 'object' ? p.last_page : undefined) ?? 1;
        itemsPerPage = (p && typeof p === 'object' ? p.per_page : undefined) ?? 10;
        totalDonations = summary?.total_requests ?? (p && typeof p === 'object' ? p.total : undefined) ?? 0;
        pendingDonations = summary?.pending_requests ?? 0;
        approvedDonations = summary?.approved_requests ?? 0;
        totalDonationValue = summary?.total_value ?? 0;
      } else {
        error = 'Failed to load donations';
        tableData = [];
        totalPages = 1;
        currentPage = 1;
      }
    } catch (err: any) {
      error = err.message || 'Failed to load donations';
      tableData = [];
      totalPages = 1;
      currentPage = 1;
    } finally {
      loading = false;
    }
  }

  function handleSearch(event: CustomEvent<string>) {
    searchPhrase = event.detail;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { selectedStatuses, selectedEmployer, selectedCharity, selectedEmployee, startDate, endDate, searchPhrase } });
    loadData();
  }

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    const requestedPage = event.detail.page;
    
    if (requestedPage !== currentPage && requestedPage > 0 && requestedPage <= totalPages) {
      currentPage = requestedPage;
      preferences.update({ currentPage });
      loadData();
    }
  }

  onMount(async () => {
    await loadData();
  });
</script>

<div class="manage-list-page">
  <div class="page-header">
    <p class="subtitle">View and manage all donation requests across the platform</p>
  </div>

  <div class="metrics-grid">
      <div class="metric-card">
        <div class="metric-label">Total requests</div>
        <div class="metric-value">{totalDonations.toLocaleString()}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">Pending</div>
        <div class="metric-value">{pendingDonations.toLocaleString()}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">Approved / completed</div>
        <div class="metric-value">{approvedDonations.toLocaleString()}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">Total Value</div>
        <div class="metric-value">{DonationService.formatCurrency(totalDonationValue)}</div>
      </div>
  </div>

  <div class="filters-section">
    <div class="filters-left">
      <div class="filter-item">
        <SearchInput
          placeholder="Search donations..."
          bind:value={searchPhrase}
          on:search={handleSearch}
          width="100%"
        />
      </div>
      
      <div class="filter-item">
        <DateRange
          bind:startDate={startDate}
          bind:endDate={endDate}
          placeholder="Date Range"
          on:apply={handleDateRangeChange}
        />
      </div>

      <div class="filter-item">
        <AsyncSearchableDropdown
          placeholder="Search employer..."
          selectedValue={selectedEmployer}
          searchFunction={searchEmployers}
          getSelectedLabel={getEmployerLabel}
          onSelectionChange={handleEmployerChange}
          debounceMs={300}
        />
      </div>

      <div class="filter-item">
        <AsyncSearchableDropdown
          placeholder="Search charity..."
          selectedValue={selectedCharity}
          searchFunction={searchCharities}
          getSelectedLabel={getCharityLabel}
          onSelectionChange={handleCharityChange}
          debounceMs={300}
        />
      </div>

      <div class="filter-item">
        <AsyncSearchableDropdown
          placeholder="Search employee..."
          selectedValue={selectedEmployee}
          searchFunction={searchEmployees}
          getSelectedLabel={getEmployeeLabel}
          onSelectionChange={handleEmployeeChange}
          debounceMs={300}
        />
      </div>

      <div class="filter-item">
        <MultiSelect
          options={statusOptionsList}
          bind:selectedValues={selectedStatuses}
          placeholder="Status"
          on:change={handleStatusChange}
        />
      </div>
    </div>
    
    <div class="filters-right">
      <Button variant="secondary" on:click={handleResetFilters}>
        Reset
      </Button>
    </div>
  </div>

  <!-- Donations Table Section -->
  <div class="table-section">
    {#if loading}
      <div class="loading-wrap">
        <LoadingSkeleton lines={10} height="2.5rem" />
      </div>
    {:else if error}
      <ErrorState 
        message="Failed to load donations" 
        description={error}
        onRetry={loadData}
      />
    {:else if tableData.length === 0}
      <EmptyState 
        message="No donations found"
        description="Try adjusting your search or filter criteria to see more results."
        actionLabel="Reset Filters"
        onAction={handleResetFilters}
      />
    {:else}
      <Table {columns} data={tableData} onRowClick={handleRowClick} />
    {/if}

    {#if totalPages > 1}
      <div class="pagination-container">
        <Pagination
          {currentPage}
          {totalPages}
          on:pageChange={handlePageChange}
        />
      </div>
    {/if}
  </div>
</div>

<style>
  .page-header {
    margin-bottom: 1.5rem;
  }

  .subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .metric-card {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 1rem 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .metric-label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
    margin: 0;
  }

  .metric-value {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
  }

  .filters-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
    width: 100%;
    box-sizing: border-box;
  }

  .filters-left {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    flex: 1;
    min-width: 0;
    flex-wrap: wrap;
    width: 100%;
  }

  .filter-item {
    flex: 1 1 auto;
    min-width: 140px;
    max-width: 300px;
  }

  .filter-item :global(.async-dropdown-container),
  .filter-item :global(.multi-select-container),
  .filter-item :global(.date-range-wrapper),
  .filter-item :global(.search-input-wrapper) {
    width: 100%;
  }

  .filters-right {
    flex-shrink: 0;
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    width: 100%;
    box-sizing: border-box;
    max-width: 100%;
  }
  
  .table-section :global(.table-container) {
    border-radius: 12px;
    box-shadow: none;
    border: none;
  }

  .loading-wrap {
    padding: 2rem;
    min-height: 320px;
  }

  .pagination-container {
    padding: 1rem 1.5rem;
    border-top: 1px solid #f3f4f6;
  }

  :global(.employee-info) {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }

  :global(.status-icon) {
    width: 1rem;
    height: 1rem;
  }

  :global(.status-approved) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.status-pending) {
    background-color: #fef3c7;
    color: #92400e;
  }

  :global(.status-rejected) {
    background-color: #fee2e2;
    color: #991b1b;
  }

  /* Canonical lifecycle status colours (single source: donationLifecycle.ts) */
  :global(.lc-pending) {
    background-color: #fef3c7;
    color: #92400e;
  }
  :global(.lc-approved) {
    background-color: #d1fae5;
    color: #065f46;
  }
  :global(.lc-completed) {
    background-color: #d1fae5;
    color: #065f46;
  }
  :global(.lc-rejected) {
    background-color: #fee2e2;
    color: #991b1b;
  }
  :global(.lc-awaiting-registration) {
    background-color: #fef3c7;
    color: #92400e;
  }

  @media (max-width: 768px) {
    .metrics-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .filters-section {
      flex-direction: column;
      gap: 1rem;
      align-items: stretch;
    }

    .filters-left {
      flex-direction: column;
      gap: 0.75rem;
    }

    .filter-item {
      min-width: auto;
      max-width: 100%;
    }

    .filters-right {
      width: 100%;
    }

    .filters-right :global(button) {
      width: 100%;
    }
  }
</style>
